/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 29th 2024, 10:32:00 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { CSSProperties } from "react";
import React from "react";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import TextareaAutosize, { TextareaAutosizeProps } from "@mui/material/TextareaAutosize";
import { EditToggler } from "./EditToggler";
import "./TextEditorWithLineNumbers.css";

type TextEditTogglerProps = {
  value: string;
  handleEdit: (newValue: string) => void;
  forceEditMode?: boolean;
  setForceEditMode?: (editMode: boolean) => void;
};

export const TextEditToggler = ({ handleEdit, value, forceEditMode, setForceEditMode }: TextEditTogglerProps) => (
  <EditToggler
    renderEditComponent={({ setValueInputRef }) => (
      <TextField
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          handleEdit(event.target.value);
          setForceEditMode?.(false);
        }}
        inputRef={setValueInputRef}
        defaultValue={value}
        fullWidth
        variant="standard"
      />
    )}
  >
    {({ toggleEditMode, editIcon }) => {
      if (forceEditMode) toggleEditMode();

      return (
        <Stack
          direction="row"
          spacing={2}
          onClick={toggleEditMode}
          sx={{ cursor: "pointer", marginRight: "auto", width: "100%" }}
        >
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
        </Stack>
      );
    }}
  </EditToggler>
);

const getLineNumbers = (text: string) => {
  const lines = text.split("\n").length;
  return Array.from({ length: lines }, (_, i) => i + 1); // Creating array from an array-like objects (objects with a length property and indexed elements). https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/from#description
};

export const TextEditorWithLineNumbers = (props: TextareaAutosizeProps) => {
  /***
   * Match line number font size with text area
   * Match line number line height with text area
   */
  const linesAndTextSyncedStyle: CSSProperties = {
    fontSize: "0.75rem",
    lineHeight: "1.5",
    paddingTop: "0.5rem",
  };

  return (
    <Stack className="editor-container" direction="row">
      <div className="line-numbers" style={{ color: props.style?.color || "", ...linesAndTextSyncedStyle }}>
        <Stack>
          {getLineNumbers((props.value || "") as string).map((lineNumber) => (
            <span>{lineNumber}</span>
          ))}
        </Stack>
      </div>
      <TextareaAutosize
        minRows={10}
        className="text-area"
        {...props}
        style={{ whiteSpace: "pre", paddingLeft: "0.8rem", ...props.style, ...linesAndTextSyncedStyle }}
      />
    </Stack>
  );
};
